class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));

    this.addEventListener('keyup', this.onKeyup.bind(this));

    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  normalize(value) {
    return value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  distance(a, b) {
    if (a === b) return 0;

    if (!a.length) return b.length;

    if (!b.length) return a.length;

    const row = Array.from(
      { length: b.length + 1 },
      (_, i) => i
    );

    for (let i = 1; i <= a.length; i++) {
      let previous = row[0];

      row[0] = i;

      for (let j = 1; j <= b.length; j++) {
        const temp = row[j];

        row[j] = Math.min(
          row[j] + 1,
          row[j - 1] + 1,
          previous + (
            a[i - 1] === b[j - 1] ? 0 : 1
          )
        );

        previous = temp;
      }
    }

    return row[b.length];
  }

  findMatchingVendor(query) {
    const vendors = Array.isArray(window.BeautyMarketSearchVendors)
      ? window.BeautyMarketSearchVendors
      : [];

    if (!vendors.length) {
      return null;
    }

    const normalizedQuery = this.normalize(query);

    if (!normalizedQuery) {
      return null;
    }

    const queryTokens = normalizedQuery.split(/\s+/);

    let exactMatch = null;

    /*
     * 1. Primero buscamos coincidencia exacta.
     * Esto evita que una búsqueda genérica como "skin"
     * sea convertida accidentalmente en una marca.
     */

    for (const vendor of vendors) {
      const vendorName = String(vendor || '').trim();

      if (!vendorName) {
        continue;
      }

      const normalizedVendor = this.normalize(vendorName);

      if (!normalizedVendor) {
        continue;
      }

      if (
        normalizedQuery === normalizedVendor ||
        queryTokens.includes(normalizedVendor)
      ) {
        exactMatch = vendorName;
        break;
      }
    }

    if (exactMatch) {
      return exactMatch;
    }

    /*
     * 2. Corrección tolerante a errores.
     *
     * Solo corregimos palabras suficientemente largas.
     * Esto evita que palabras cortas como:
     *
     * skin
     * crema
     * ojos
     * etc.
     *
     * sean confundidas con marcas.
     */

    let bestVendor = null;
    let bestDistance = Infinity;

    for (const vendor of vendors) {
      const vendorName = String(vendor || '').trim();

      if (!vendorName) {
        continue;
      }

      const normalizedVendor = this.normalize(vendorName);

      if (normalizedVendor.length < 4) {
        continue;
      }

      const vendorTokens = normalizedVendor.split(/\s+/);

      /*
       * Para marcas de varias palabras comprobamos
       * la consulta completa.
       *
       * Para marcas de una palabra comprobamos cada
       * palabra de la búsqueda.
       */

      const candidates =
        vendorTokens.length === 1
          ? queryTokens
          : [normalizedQuery];

      for (const candidate of candidates) {
        if (candidate.length < 4) {
          continue;
        }

        const distance = this.distance(
          candidate,
          normalizedVendor
        );

        let allowedDistance = 1;

        if (normalizedVendor.length >= 8) {
          allowedDistance = 2;
        }

        /*
         * Evita correcciones demasiado agresivas.
         */

        const similarity =
          1 -
          distance /
            Math.max(
              candidate.length,
              normalizedVendor.length
            );

        if (
          distance <= allowedDistance &&
          similarity >= 0.80 &&
          distance < bestDistance
        ) {
          bestVendor = vendorName;
          bestDistance = distance;
        }
      }
    }

    return bestVendor;
  }

  canonicalizeQuery(query) {
    const matchingVendor = this.findMatchingVendor(query);

    /*
     * Si no encontramos una marca,
     * dejamos la búsqueda exactamente como estaba.
     */

    if (!matchingVendor) {
      return query;
    }

    const normalizedVendor = this.normalize(
      matchingVendor
    );

    const originalTokens = query.split(/\s+/);

    const remainingTokens = originalTokens.filter(
      (token) => {
        const normalizedToken = this.normalize(token);

        if (!normalizedToken) {
          return false;
        }

        if (normalizedToken === normalizedVendor) {
          return false;
        }

        const distance = this.distance(
          normalizedToken,
          normalizedVendor
        );

        const allowedDistance =
          normalizedVendor.length >= 8 ? 2 : 1;

        return distance > allowedDistance;
      }
    );

    /*
     * Si la búsqueda es solamente la marca:
     *
     * lureal
     *
     * se transforma en:
     *
     * vendor:"L'Oréal"
     */

    if (!remainingTokens.length) {
      return `vendor:"${matchingVendor}"`;
    }

    /*
     * Si hay marca + producto:
     *
     * lureal mascara
     *
     * se transforma en:
     *
     * vendor:"L'Oréal" mascara
     */

    return `vendor:"${matchingVendor}" ${remainingTokens.join(' ')}`;
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    if (!query.length) {
      event.preventDefault();
      return;
    }

    /*
     * Si el usuario seleccionó directamente
     * un resultado del predictive search,
     * dejamos que Dawn continúe normalmente.
     */

    if (this.querySelector('[aria-selected="true"] a')) {
      return;
    }

    const canonicalQuery =
      this.canonicalizeQuery(query);

    /*
     * Si no hubo corrección,
     * dejamos que Shopify haga su búsqueda normal.
     */

    if (canonicalQuery === query) {
      return;
    }

    /*
     * Si encontramos una marca con error ortográfico,
     * corregimos la consulta antes de enviarla.
     */

    event.preventDefault();

    const form = this.input.form;

    const params = new URLSearchParams(
      new FormData(form)
    );

    params.set('q', canonicalQuery);

    window.location.href =
      `${form.action}?${params.toString()}`;
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (
      !this.searchTerm ||
      !newSearchTerm.startsWith(this.searchTerm)
    ) {
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.updateSearchForTerm(
      this.searchTerm,
      newSearchTerm
    );

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(
      this.searchTerm
    );
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';

      this.abortController.abort();

      this.abortController =
        new AbortController();

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentQuery = this.getQuery();

    if (!currentQuery.length) {
      return;
    }

    if (this.searchTerm !== currentQuery) {
      this.onChange();
    } else if (
      this.getAttribute('results') === 'true'
    ) {
      this.open();
    } else {
      this.getSearchResults(
        this.searchTerm
      );
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    if (event.code === 'ArrowUp') {
      this.switchOption('up');
    }

    if (event.code === 'ArrowDown') {
      this.switchOption('down');
    }

    if (event.code === 'Enter') {
      this.selectOption();
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(
    previousTerm,
    newTerm
  ) {
    const element = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );

    const currentText =
      element?.innerText;

    if (!currentText) {
      return;
    }

    const matches =
      currentText.match(
        new RegExp(previousTerm, 'g')
      );

    if (
      matches &&
      matches.length > 1
    ) {
      return;
    }

    element.innerText =
      currentText.replace(
        previousTerm,
        newTerm
      );
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) {
      return;
    }

    const moveUp =
      direction === 'up';

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    const visible = Array.from(
      this.querySelectorAll(
        'li, button.predictive-search__item'
      )
    ).filter(
      (element) =>
        element.offsetParent !== null
    );

    if (
      moveUp &&
      !selected
    ) {
      return;
    }

    let selectedIndex =
      visible.indexOf(selected);

    let activeIndex = 0;

    if (
      !moveUp &&
      selected
    ) {
      activeIndex =
        selectedIndex ===
        visible.length - 1
          ? 0
          : selectedIndex + 1;
    } else if (moveUp) {
      activeIndex =
        selectedIndex === 0
          ? visible.length - 1
          : selectedIndex - 1;
    }

    if (
      activeIndex === selectedIndex ||
      !visible[activeIndex]
    ) {
      return;
    }

    const active =
      visible[activeIndex];

    active.setAttribute(
      'aria-selected',
      true
    );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      active.id
    );
  }

  selectOption() {
    const selected =
      this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );

    if (selected) {
      selected.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey =
      searchTerm
        .replace(/\s+/g, '-')
        .toLowerCase();

    this.setLiveRegionLoadingState();

    if (
      this.cachedResults[queryKey]
    ) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      return;
    }

    /*
     * Shopify Predictive Search.
     *
     * Se mantienen los campos nativos:
     * title
     * product_type
     * variants.title
     * vendor
     *
     * La tolerancia de errores de Shopify
     * sigue funcionando para el predictive search.
     */

    fetch(
      `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=product` +
      `&resources[limit]=10` +
      `&resources[limit_scope]=each` +
      `&resources[options][unavailable_products]=last` +
      `&resources[options][fields]=title,product_type,variants.title,vendor` +
      `&section_id=predictive-search`,
      {
        signal:
          this.abortController.signal
      }
    )
      .then((response) => {
        if (!response.ok) {
          this.close();

          throw new Error(
            response.status
          );
        }

        return response.text();
      })
      .then((text) => {
        const node =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            )
            .querySelector(
              '#shopify-section-predictive-search'
            );

        if (!node) {
          return;
        }

        const markup =
          node.innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (instance) => {
            instance.cachedResults[
              queryKey
            ] = markup;
          }
        );

        this.renderSearchResults(
          markup
        );
      })
      .catch((error) => {
        if (error?.code !== 20) {
          this.close();
        }
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    this.setLiveRegionText(
      this.loadingText
    );

    this.setAttribute(
      'loading',
      true
    );
  }

  setLiveRegionText(text) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      text;

    setTimeout(() => {
      this.statusElement?.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML =
      markup;

    this.setAttribute(
      'results',
      true
    );

    this.removeAttribute(
      'loading'
    );

    this.setLiveRegionText(
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      )?.textContent || ''
    );

    this.open();
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${window.innerHeight -
        (
          document
            .querySelector(
              '.section-header'
            )
            ?.getBoundingClientRect()
            .bottom || 0
        )
      }px`;

    this.setAttribute(
      'open',
      true
    );

    this.input.setAttribute(
      'aria-expanded',
      true
    );

    this.isOpen = true;
  }

  close(clear = false) {
    this.closeResults(clear);

    this.isOpen = false;
  }

  closeResults(clear = false) {
    if (clear) {
      this.input.value = '';

      this.removeAttribute(
        'results'
      );
    }

    this.querySelector(
      '[aria-selected="true"]'
    )?.setAttribute(
      'aria-selected',
      false
    );

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute(
      'loading'
    );

    this.removeAttribute(
      'open'
    );

    this.input.setAttribute(
      'aria-expanded',
      false
    );

    this.predictiveSearchResults.removeAttribute(
      'style'
    );
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);